<script lang="ts" setup>
const props = defineProps({
  feature: {
    default: undefined,
    type: String as PropType<SegmentationFeature>,
  },
  listName: {
    default: '',
    type: String,
  },
  modelValue: {
    default: 0,
    type: Number,
  },
  query: {
    default: () => [],
    type: Array as PropType<SegmentationCondition[]>,
  },
})

const emit = defineEmits<{
  'selected': [listId: number]
  'update:modelValue': [modelValue?: number]
}>()

const { generateNameFromQuery } = useSegmentation()
const { t } = useI18n()
const { createToast } = useToasts()
const listsStore = useListsStore()

const selectMenuRef = ref()
const creating = ref(false)

const options = computed((): USelectMenuOption[] => {
  return listsStore.lists.map(list => ({
    id: list.id,
    label: list.name,
    selected: props.modelValue === list.id,
    value: list.id,
  }))
})

const selectedListName = computed(() => {
  if (props.modelValue) {
    const list = listsStore.lists.find(list => list.id === props.modelValue)

    return list?.name || ''
  }

  return ''
})

const defaultListName = computed(() => {
  if (props.listName) {
    return props.listName
  }
  else if (props.query.length) {
    return generateNameFromQuery(props.query)
  }

  return ''
})

function clearInput() {
  if (import.meta.client) {
    try {
      selectMenuRef.value.onQueryChange({ target: { value: '' } })
    }
    catch {}
  }
}

async function onChange(value: string | USelectMenuOption) {
  creating.value = true

  try {
    let listId: number

    // Create a new list as the option is the input value
    if (typeof value === 'string') {
      const newList = await listsStore.createList({
        imported: props.feature === 'enrichment',
        name: value,
      })

      listId = newList.id

      createToast({ message: t('listCreated') })
    }
    else {
      listId = value.id || value.value
    }

    emit('update:modelValue', listId)
    emit('selected', listId)
  }
  catch {
    createToast({
      message: 'somethingWentWrong',
      status: 'error',
    })
  }

  clearInput()
  creating.value = false
}
</script>

<template>
  <USelectMenu
    ref="selectMenuRef"
    creatable
    :icon="getIcon('lists')"
    :loading="creating"
    :model-value="selectedListName"
    :options="options"
    :popper="{ strategy: 'absolute' }"
    :required="false"
    searchable
    :searchable-placeholder="listsStore.lists.length ? t('searchOrCreate') : t('createList')"
    :ui-menu="{
      container: 'min-w-48',
      empty: 'w-full overflow-x-auto',
      option: {
        base: 'w-full overflow-x-auto',
      },
    }"
    @change="onChange"
  >
    <template #label>
      <span class="truncate">
        {{ selectedListName || t('selectList') }}
      </span>
    </template>

    <template #option="{ option }">
      <div class="flex items-center gap-2 overflow-x-auto text-sm">
        <UIcon
          class="size-5 flex-shrink-0"
          :class="{ 'text-green-600 dark:text-green-400': option.selected }"
          :name="getIcon(option.selected ? 'check' : 'list')"
        />
        {{ option.label }}
      </div>
    </template>

    <template #option-create="{ option }">
      <div class="flex items-center gap-2">
        <UIcon
          class="size-5 flex-shrink-0"
          :name="getIcon('list')"
        />

        <span class="text-sm">
          {{ t('createList') }}: <span class="font-bold">{{ typeof option === 'string' ? option || defaultListName : option.label || defaultListName }}</span>
        </span>
      </div>
    </template>

    <template #empty>
      <div class="w-full overflow-x-auto">
        {{ t('typeToCreate') }}
      </div>
    </template>
  </USelectMenu>
</template>

<i18n lang="json">
{
  "en": {
    "createList": "Create a new list",
    "listCreated": "List created successfully",
    "searchOrCreate": "Search or create a new list",
    "selectList": "Select a list",
    "typeToCreate": "Type something to create a new list"
  },
  "es": {
    "createList": "Crear una nueva lista",
    "listCreated": "Lista creada exitosamente",
    "searchOrCreate": "Buscar o crear una nueva lista",
    "selectList": "Seleccione una lista",
    "typeToCreate": "Escribe algo para crear una nueva lista"
  },
  "fr": {
    "createList": "Créer une nouvelle liste",
    "listCreated": "Liste créée avec succès",
    "searchOrCreate": "Rechercher ou créer une nouvelle liste",
    "selectList": "Sélectionnez une liste",
    "typeToCreate": "Tapez quelque chose pour créer une nouvelle liste"
  }
}
</i18n>
